/**
 * CreativesTab — Creative Performance Grid for TJ Command Center Phase 1.5B
 *
 * Shows ad-level performance with creative previews, auto-labels
 * (Winner/Fatigued/Underperformer/New), and comparison modal.
 */

import { useState, useMemo } from "react";
import {
  useAdCreatives,
  useAdCampaigns,
  useAdPerformanceAllLevels,
  aggregateSnapshots,
  type AdCreative,
  type AggregatedMetrics,
} from "@/hooks/useAdPerformance";
import {
  overlaySystemLeadsOnSnapshots,
  type SystemLeadsOverlay,
} from "@/hooks/useSystemLeads";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Loader2,
  Image as ImageIcon,
  Video,
  LayoutGrid,
  ArrowUpDown,
  GitCompareArrows,
} from "lucide-react";

// ─── Types ──────────────────────────────────────────────

interface CreativeRow {
  creative: AdCreative;
  campaignName: string;
  metrics: AggregatedMetrics;
  label: "winner" | "fatigued" | "underperformer" | "new";
}

// ─── Helpers ────────────────────────────────────────────

function formatCurrency(value: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat("en-US").format(Math.round(value));
}

function formatPct(value: number): string {
  return `${value.toFixed(2)}%`;
}

function getCreativeLabel(
  metrics: AggregatedMetrics,
  campaignAvgCPL: number,
  campaignAvgCTR: number
): CreativeRow["label"] {
  if (metrics.totalSpend < 10) return "new";
  if (metrics.avgFrequency > 3.0) return "fatigued";
  if (metrics.qualityRanking?.toUpperCase().includes("BELOW")) return "fatigued";
  if (campaignAvgCPL > 0 && metrics.avgCPL > campaignAvgCPL * 1.5) return "underperformer";
  if (
    campaignAvgCPL > 0 &&
    campaignAvgCTR > 0 &&
    metrics.avgCPL < campaignAvgCPL &&
    metrics.avgCTR > campaignAvgCTR
  )
    return "winner";
  if (metrics.totalLeads === 0 && metrics.totalSpend > 20) return "underperformer";
  return "new";
}

const labelConfig = {
  winner: { text: "Winner", className: "bg-green-50 text-green-700 border-green-200" },
  fatigued: { text: "Fatigued", className: "bg-red-50 text-red-700 border-red-200" },
  underperformer: { text: "Underperformer", className: "bg-yellow-50 text-yellow-700 border-yellow-200" },
  new: { text: "New", className: "bg-blue-50 text-blue-700 border-blue-200" },
};

function CreativeTypeIcon({ type }: { type: string | null }) {
  if (type === "VIDEO") return <Video className="h-4 w-4 text-purple-500" />;
  if (type === "CAROUSEL") return <LayoutGrid className="h-4 w-4 text-blue-500" />;
  return <ImageIcon className="h-4 w-4 text-gray-500" />;
}

function FrequencyBadge({ value }: { value: number }) {
  if (value <= 0) return <span className="text-muted-foreground">—</span>;
  const color =
    value > 4.0
      ? "text-red-700 bg-red-50 border-red-200"
      : value > 3.0
      ? "text-yellow-700 bg-yellow-50 border-yellow-200"
      : "text-green-700 bg-green-50 border-green-200";
  return (
    <span className={`text-xs px-1.5 py-0.5 rounded border ${color}`}>
      {value.toFixed(1)}
    </span>
  );
}

// ─── Comparison Modal ───────────────────────────────────

function ComparisonModal({
  open,
  onClose,
  rows,
}: {
  open: boolean;
  onClose: () => void;
  rows: CreativeRow[];
}) {
  if (rows.length < 2) return null;

  const metricKeys: { key: keyof AggregatedMetrics; label: string; format: (v: number) => string; lowerIsBetter?: boolean }[] = [
    { key: "totalSpend", label: "Spend", format: formatCurrency },
    { key: "totalLeads", label: "Leads", format: formatNumber },
    { key: "avgCPL", label: "CPL", format: formatCurrency, lowerIsBetter: true },
    { key: "avgCTR", label: "CTR", format: formatPct },
    { key: "avgCPC", label: "CPC", format: formatCurrency, lowerIsBetter: true },
    { key: "avgCPM", label: "CPM", format: formatCurrency, lowerIsBetter: true },
    { key: "avgFrequency", label: "Frequency", format: (v) => v.toFixed(1), lowerIsBetter: true },
    { key: "totalImpressions", label: "Impressions", format: formatNumber },
    { key: "totalReach", label: "Reach", format: formatNumber },
    { key: "totalClicks", label: "Clicks", format: formatNumber },
  ];

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Creative Comparison</DialogTitle>
        </DialogHeader>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2 pr-4 font-medium text-muted-foreground">Metric</th>
                {rows.map((r) => (
                  <th key={r.creative.meta_ad_id} className="text-right py-2 px-3 font-medium max-w-[180px] truncate">
                    {r.creative.meta_ad_name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {metricKeys.map(({ key, label, format, lowerIsBetter }) => {
                const values = rows.map((r) => r.metrics[key] as number);
                const best = lowerIsBetter
                  ? Math.min(...values.filter((v) => v > 0))
                  : Math.max(...values);

                return (
                  <tr key={key} className="border-b last:border-0">
                    <td className="py-2 pr-4 text-muted-foreground">{label}</td>
                    {rows.map((r, i) => {
                      const val = values[i];
                      const isBest = val === best && val > 0;
                      return (
                        <td
                          key={r.creative.meta_ad_id}
                          className={`text-right py-2 px-3 font-mono ${isBest ? "font-bold text-green-700" : ""}`}
                        >
                          {val > 0 ? format(val) : "—"}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
              <tr className="border-b">
                <td className="py-2 pr-4 text-muted-foreground">Label</td>
                {rows.map((r) => (
                  <td key={r.creative.meta_ad_id} className="text-right py-2 px-3">
                    <Badge variant="outline" className={labelConfig[r.label].className}>
                      {labelConfig[r.label].text}
                    </Badge>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Main Component ─────────────────────────────────────

export function CreativesTab({
  dateFrom,
  dateTo,
  systemLeadsOverlay,
}: {
  dateFrom: string;
  dateTo: string;
  systemLeadsOverlay?: SystemLeadsOverlay;
}) {
  const creatives = useAdCreatives();
  const campaigns = useAdCampaigns();
  const { data: rawSnapshots = [], isLoading } = useAdPerformanceAllLevels({ dateFrom, dateTo });

  // Overlay system leads onto Meta pixel data
  const allSnapshots = useMemo(
    () => systemLeadsOverlay ? overlaySystemLeadsOnSnapshots(rawSnapshots, systemLeadsOverlay) : rawSnapshots,
    [rawSnapshots, systemLeadsOverlay]
  );
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [compareOpen, setCompareOpen] = useState(false);
  const [sortBy, setSortBy] = useState<"spend" | "cpl" | "ctr" | "frequency">("spend");

  // Build creative rows with metrics + labels
  const creativeRows = useMemo(() => {
    if (!creatives.data || creatives.data.length === 0) return [];

    // Group ad-level snapshots by meta_ad_id
    const adSnaps = new Map<string, typeof allSnapshots>();
    for (const snap of allSnapshots) {
      if (snap.level === "ad" && snap.meta_ad_id) {
        const arr = adSnaps.get(snap.meta_ad_id) ?? [];
        arr.push(snap);
        adSnaps.set(snap.meta_ad_id, arr);
      }
    }

    // Campaign-level averages for labeling
    const campaignSnaps = new Map<string, typeof allSnapshots>();
    for (const snap of allSnapshots) {
      if (snap.level === "campaign" && snap.meta_campaign_id) {
        const arr = campaignSnaps.get(snap.meta_campaign_id) ?? [];
        arr.push(snap);
        campaignSnaps.set(snap.meta_campaign_id, arr);
      }
    }
    const campaignMetrics = new Map<string, AggregatedMetrics>();
    for (const [cid, snaps] of campaignSnaps) {
      campaignMetrics.set(cid, aggregateSnapshots(snaps));
    }

    // Internal campaign ID → meta campaign ID mapping
    const internalToMetaCampaign = new Map<string, string>();
    for (const c of campaigns.data ?? []) {
      internalToMetaCampaign.set(c.id, c.meta_campaign_id);
    }

    // Internal campaign ID → name
    const campaignNames = new Map<string, string>();
    for (const c of campaigns.data ?? []) {
      campaignNames.set(c.id, c.meta_campaign_name);
    }

    const rows: CreativeRow[] = [];
    for (const creative of creatives.data) {
      const snaps = adSnaps.get(creative.meta_ad_id) ?? [];
      const metrics = aggregateSnapshots(snaps);
      const metaCampaignId = internalToMetaCampaign.get(creative.campaign_id);
      const cMetrics = metaCampaignId ? campaignMetrics.get(metaCampaignId) : undefined;
      const campaignName = campaignNames.get(creative.campaign_id) ?? "Unknown";

      const label = getCreativeLabel(
        metrics,
        cMetrics?.avgCPL ?? 0,
        cMetrics?.avgCTR ?? 0
      );

      rows.push({ creative, campaignName, metrics, label });
    }

    // Sort
    rows.sort((a, b) => {
      switch (sortBy) {
        case "cpl": return (a.metrics.avgCPL || 999999) - (b.metrics.avgCPL || 999999);
        case "ctr": return b.metrics.avgCTR - a.metrics.avgCTR;
        case "frequency": return b.metrics.avgFrequency - a.metrics.avgFrequency;
        default: return b.metrics.totalSpend - a.metrics.totalSpend;
      }
    });

    return rows;
  }, [creatives.data, campaigns.data, allSnapshots, sortBy]);

  const selectedRows = creativeRows.filter((r) => selectedIds.has(r.creative.meta_ad_id));

  const toggleSelection = (metaAdId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(metaAdId)) next.delete(metaAdId);
      else if (next.size < 4) next.add(metaAdId);
      return next;
    });
  };

  if (isLoading || creatives.isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (creativeRows.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
        <ImageIcon className="h-12 w-12 mb-4" />
        <p className="text-lg font-medium">No creatives found</p>
        <p className="text-sm mt-1">Sync your Meta Ads account to see creative performance here.</p>
      </div>
    );
  }

  // Fatigue warning
  const fatiguedCount = creativeRows.filter((r) => r.label === "fatigued").length;

  return (
    <div className="space-y-4">
      {/* Fatigue banner */}
      {fatiguedCount > 0 && (
        <div className="rounded-md bg-yellow-50 border border-yellow-200 p-3 text-sm text-yellow-800">
          <strong>{fatiguedCount} ad{fatiguedCount > 1 ? "s" : ""} showing fatigue</strong> — frequency above 3.0 or below-average quality ranking. Consider refreshing creative.
        </div>
      )}

      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">{creativeRows.length} creatives</span>
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              setSortBy((prev) =>
                prev === "spend" ? "cpl" : prev === "cpl" ? "ctr" : prev === "ctr" ? "frequency" : "spend"
              )
            }
          >
            <ArrowUpDown className="h-3.5 w-3.5 mr-1" />
            Sort: {sortBy === "spend" ? "Spend" : sortBy === "cpl" ? "CPL" : sortBy === "ctr" ? "CTR" : "Frequency"}
          </Button>
        </div>
        {selectedIds.size >= 2 && (
          <Button size="sm" onClick={() => setCompareOpen(true)}>
            <GitCompareArrows className="h-3.5 w-3.5 mr-1" />
            Compare ({selectedIds.size})
          </Button>
        )}
      </div>

      {/* Creative Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {creativeRows.map((row) => {
          const m = row.metrics;
          const cfg = labelConfig[row.label];
          const isSelected = selectedIds.has(row.creative.meta_ad_id);

          return (
            <Card
              key={row.creative.meta_ad_id}
              className={`relative ${isSelected ? "ring-2 ring-primary" : ""}`}
            >
              <CardContent className="pt-4 pb-3 space-y-3">
                {/* Header: checkbox + type + name + label */}
                <div className="flex items-start gap-2">
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={() => toggleSelection(row.creative.meta_ad_id)}
                    className="mt-0.5"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <CreativeTypeIcon type={row.creative.creative_type} />
                      <span className="font-medium text-sm truncate">{row.creative.meta_ad_name}</span>
                    </div>
                    <p className="text-xs text-muted-foreground truncate mt-0.5">{row.campaignName}</p>
                  </div>
                  <Badge variant="outline" className={`shrink-0 text-xs ${cfg.className}`}>
                    {cfg.text}
                  </Badge>
                </div>

                {/* Preview thumbnail */}
                {row.creative.creative_preview_url && (
                  <div className="rounded-md overflow-hidden bg-muted h-32 flex items-center justify-center">
                    <img
                      src={row.creative.creative_preview_url}
                      alt={row.creative.meta_ad_name}
                      className="max-h-32 max-w-full object-contain"
                      loading="lazy"
                    />
                  </div>
                )}

                {/* Ad copy snippet */}
                {row.creative.creative_body && (
                  <p className="text-xs text-muted-foreground line-clamp-2">
                    {row.creative.creative_body}
                  </p>
                )}

                {/* Metrics grid */}
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div>
                    <p className="text-muted-foreground">Spend</p>
                    <p className="font-semibold">{formatCurrency(m.totalSpend)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Leads</p>
                    <p className="font-semibold">{formatNumber(m.totalLeads)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">CPL</p>
                    <p className="font-semibold">{m.totalLeads > 0 ? formatCurrency(m.avgCPL) : "—"}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">CTR</p>
                    <p className="font-semibold">{formatPct(m.avgCTR)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">CPC</p>
                    <p className="font-semibold">{m.totalClicks > 0 ? formatCurrency(m.avgCPC) : "—"}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Freq.</p>
                    <p className="font-semibold"><FrequencyBadge value={m.avgFrequency} /></p>
                  </div>
                </div>

                {/* Status */}
                {row.creative.status && (
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>Status: {row.creative.status}</span>
                    <span>{row.creative.creative_type}</span>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Comparison Modal */}
      <ComparisonModal
        open={compareOpen}
        onClose={() => setCompareOpen(false)}
        rows={selectedRows}
      />
    </div>
  );
}
